import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@src/core/hooks';
import {
  fixEmailSelector,
  codeResentSelector,
  passwordRecoveryEmailSelector,
  passwordRecoveryCodeSelector,
  setCodeResent,
  setResetPasswordCode,
  setResetPasswordEmail,
  setFixEmail,
} from '@src/features/customers/auth/store/auth';
import { setStepCheckout } from '@src/stores/checkout';
import { useWidgetId } from '@src/core/IdProvider';
import { track } from '@src/services/analytics';
import { nextRecoveryStep } from '@src/features/customers/auth/password-recovery/core/nextRecoveryStep';
import { RECOVERY_STEP, type RecoveryStep } from '@src/features/customers/types';

type UseRecoveryFlowOptions = {
  advanceOnCodeValid?: boolean;
};

export function useRecoveryFlow(onBack: () => void, options?: UseRecoveryFlowOptions) {
  const advanceOnCodeValid = options?.advanceOnCodeValid ?? true;
  const id = useWidgetId();
  const dispatch = useAppDispatch();
  const fixEmail = useAppSelector(fixEmailSelector);
  const codeResent = useAppSelector(codeResentSelector);
  const passwordRecoveryEmail = useAppSelector(passwordRecoveryEmailSelector);
  const passwordRecoveryCode = useAppSelector(passwordRecoveryCodeSelector);
  const [step, setStep] = useState<RecoveryStep>(RECOVERY_STEP.PASSWORD_RESET);

  useEffect(() => {
    dispatch(setStepCheckout({ id, value: '' }));
  }, [id, dispatch]);

  useEffect(() => {
    track('recoveryPasswordStarted');
  }, []);

  // The flow can also be left from outside (e.g. the checkout header back button), so the recovery
  // email/code are dropped on unmount; otherwise the next entry would skip straight to the code step.
  useEffect(() => {
    return () => {
      dispatch(setResetPasswordCode(undefined));
      dispatch(setResetPasswordEmail(undefined));
    };
  }, [dispatch]);

  useEffect(() => {
    const next = nextRecoveryStep({
      current: step,
      codeResent,
      fixEmail,
      hasRecoveryEmail: Boolean(passwordRecoveryEmail),
      hasRecoveryCode: advanceOnCodeValid && Boolean(passwordRecoveryCode),
    });

    if (next && next !== step) setStep(next);

    if (fixEmail || !passwordRecoveryEmail) dispatch(setFixEmail(false));
    if (passwordRecoveryCode && step === RECOVERY_STEP.VALID_CODE) dispatch(setCodeResent(false));
  }, [advanceOnCodeValid, codeResent, dispatch, fixEmail, passwordRecoveryCode, passwordRecoveryEmail, step]);

  const goBack = () => {
    dispatch(setResetPasswordCode(undefined));
    dispatch(setResetPasswordEmail(undefined));
    onBack();

    track('recoveryPasswordBackClick');
  };

  const goToPasswordStep = () => {
    if (!passwordRecoveryCode) return;
    setStep(RECOVERY_STEP.PASSWORD_RECOVER);
  };

  return { step, goBack, goToPasswordStep };
}
